import { useCallback, useEffect, useState } from "react";

import { logger } from "@/infrastructure/lib/logger";
import { BackgroundTasksService } from "../services/backgroundTasksService";
import { BACKGROUND_TASKS_CHANGED_EVENT } from "../types/backgroundTasks";

/** Fora do Playground não há sala SSE aberta: a marca relê neste intervalo. */
const POLL_MS = 30_000;

/**
 * Quantas conversas do workspace têm algo acontecendo — o agente respondendo ou tarefa em segundo
 * plano (AB#86889): a bolinha do Chat no menu lateral, que fica fora do Playground. Relê ao montar,
 * ao voltar para a aba, a cada 30 s com a aba visível e na hora em que o Playground avisa uma mudança.
 */
export function useChatActivity(productId: string | null | undefined, enabled = true): number {
  const [count, setCount] = useState(0);

  const refresh = useCallback(async () => {
    if (!productId) {
      setCount(0);
      return;
    }
    try {
      setCount((await BackgroundTasksService.activity(productId)).length);
    } catch (error) {
      // A marca fica com o último número conhecido; a próxima leitura corrige.
      logger.warn("[useChatActivity] Could not read the chat activity", { error });
    }
  }, [productId]);

  useEffect(() => {
    if (!enabled) {
      setCount(0);
      return;
    }
    void refresh();
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void refresh();
    }, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    const onChanged = () => void refresh();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener(BACKGROUND_TASKS_CHANGED_EVENT, onChanged);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener(BACKGROUND_TASKS_CHANGED_EVENT, onChanged);
    };
  }, [enabled, refresh]);

  return count;
}
